import { useMemo, useState } from "react";

// A print-ready QR poster for the Auto Shops landing page.
// Visit /qr-poster in the browser, then either right-click the QR image and
// "Save image as…" or use your OS Print dialog and choose "Save as PDF".
export default function QrPosterPage() {
    const defaultUrl =
        typeof window !== "undefined"
            ? `${window.location.origin}/auto-shops`
            : "/auto-shops";
    const [url, setUrl] = useState(defaultUrl);

    // Free, reliable QR image service — high error correction, dark navy on white.
    const qrSrc = useMemo(() => {
        const params = new URLSearchParams({
            data: url,
            size: "1200x1200",
            ecc: "H",
            margin: "10",
            color: "0A1428",
            bgcolor: "FFFFFF",
            format: "png",
        });
        return `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
    }, [url]);

    return (
        <main data-testid="qr-poster-page" className="min-h-screen bg-white text-[#0A1428]">
            {/* Screen-only controls */}
            <div className="print:hidden max-w-4xl mx-auto px-6 md:px-12 pt-10 md:pt-16">
                <a href="/" className="text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                    ← Back to site
                </a>
                <h1 className="mt-4 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter">
                    Auto Shops QR Poster
                </h1>
                <p className="mt-4 text-slate-600 text-base md:text-lg max-w-2xl">
                    Right-click the code below and choose <strong>Save image as…</strong> to drop
                    into your brochure, or press <strong>Cmd/Ctrl + P</strong> and choose
                    <strong> Save as PDF</strong> for a full-page printable version.
                </p>

                <div className="mt-6 flex flex-col md:flex-row gap-3 md:items-center">
                    <label className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500">
                        Points to
                    </label>
                    <input
                        data-testid="qr-url-input"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        className="flex-1 border border-slate-300 px-3 py-2 font-mono text-sm"
                    />
                    <a
                        data-testid="qr-download-link"
                        href={qrSrc}
                        download="kairos-auto-shops-qr.png"
                        className="inline-flex items-center justify-center bg-[#F0493E] text-white font-bold px-5 py-2 text-sm uppercase tracking-wider"
                    >
                        Download PNG
                    </a>
                </div>
            </div>

            {/* Poster canvas — the ONLY thing that prints */}
            <section className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16">
                <div className="border-4 border-[#0A1428] p-8 md:p-14 bg-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#F0493E]">
                                Kairos ORD LLC · For Auto Shops
                            </div>
                            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-5xl leading-[0.95] tracking-tighter">
                                Never run out
                                <br />
                                of bulk oil again.
                            </h2>
                        </div>
                        <div className="hidden md:block text-right">
                            <div className="font-display font-extrabold text-2xl">KAIROS</div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-500">
                                ORD LLC
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                        <div className="flex justify-center">
                            <img
                                data-testid="qr-image"
                                src={qrSrc}
                                alt="QR code — scan to open Auto Shops page"
                                className="w-[300px] h-[300px] md:w-[380px] md:h-[380px] object-contain"
                            />
                        </div>
                        <div>
                            <p className="text-lg md:text-xl leading-relaxed text-slate-700">
                                Scan this code to see how Kairos monitors your bulk oil supply by
                                weight and alerts your team the moment inventory drops.
                            </p>
                            <div className="mt-6">
                                <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-slate-500">
                                    Scan or visit
                                </div>
                                <div className="mt-1 font-mono text-sm md:text-base break-all text-[#0A1428]">
                                    {url}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 pt-6 border-t border-slate-200 flex justify-between text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">
                        <span>kairosord.org</span>
                        <span>Weight-based bulk oil monitoring</span>
                    </div>
                </div>
            </section>
        </main>
    );
}
